import React, { useState, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQuery } from 'react-query';
import {
  Dialog,
  DialogContent,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  LinearProgress,
  Tabs,
  Tab,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CloudUpload,
  InsertDriveFile,
  PictureAsPdf,
  Image as ImageIcon,
  Close as CloseIcon,
  CheckCircle,
  Description,
  VideoFile,
  Search,
  FolderOpen
} from '@mui/icons-material';
import client from '../../API';
import API from '../../API';
import { getFileType } from '../../Utils/constants';

// File type configuration
const FILE_TYPE_CONFIG = {
  brief: { icon: Description, color: '#2196F3' },
  contract: { icon: Description, color: '#4CAF50' },
  image: { icon: ImageIcon, color: '#FF9800' },
  video: { icon: VideoFile, color: '#9C27B0' },
  file: { icon: InsertDriveFile, color: '#607D8B' },
  pdf: { icon: PictureAsPdf, color: '#F44336' }
};

const FILE_SIZE_LIMIT = 4.5 * 1024 * 1024;
const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'];

const isImageFile = (filename = '') =>
  IMAGE_EXTENSIONS.some((ext) => filename.toLowerCase().endsWith(ext));

const determineFileType = (filename = '') => {
  if (!filename) return 'misc';
  const lower = filename.toLowerCase();
  if (lower.endsWith('.pdf')) return 'pdf';
  if (isImageFile(filename)) return 'image';
  return 'misc';
};

const filterFilesByCategory = (files = [], category) => {
  if (!files.length || category === 'all') return files;
  return files.filter((file) => determineFileType(file.filename) === category);
};

const getFileUrl = (file) => {
  if (!file) return null;
  return file.file_url || file.url || null;
};

const FileLibraryIcon = ({ file }) => {
  const fileType = determineFileType(file.filename);
  const url = getFileUrl(file);

  if (fileType === 'image' && url) {
    return (
      <Avatar
        variant="rounded"
        sx={{ width: 50, height: 50, bgcolor: 'transparent', boxShadow: 1, border: '1px solid', borderColor: 'divider' }}
        src={url}
      >
        <ImageIcon sx={{ fontSize: 28 }} color="primary" />
      </Avatar>
    );
  }

  const Icon = fileType === 'pdf' ? PictureAsPdf : InsertDriveFile;
  return (
    <Avatar
      variant="rounded"
      sx={{ width: 50, height: 50, bgcolor: 'background.paper', boxShadow: 1, border: '1px solid', borderColor: 'divider' }}
    >
      <Icon sx={{ fontSize: 28 }} color={fileType === 'pdf' ? 'error' : 'action'} />
    </Avatar>
  );
};

const FilePreview = ({ url, fileName }) => {
  const fileType = getFileType(url);

  if (fileType === 'image') {
    return (
      <Box sx={{ position: 'relative', borderRadius: 3, overflow: 'hidden', width: 120, height: 120, boxShadow: '0 8px 32px rgba(0,0,0,0.1)', border: '2px solid rgba(255,255,255,0.2)', mx: 'auto' }}>
        <img src={url} alt={fileName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(45deg, rgba(76,175,80,0.1), rgba(33,150,243,0.1))' }} />
      </Box>
    );
  }

  const config = FILE_TYPE_CONFIG[fileType] || FILE_TYPE_CONFIG.file;
  const Icon = config.icon;

  return (
    <Box sx={{ width: 120, height: 120, borderRadius: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.1)', border: '2px solid rgba(255,255,255,0.2)', mx: 'auto' }}>
      <Icon sx={{ fontSize: 48, color: 'white', opacity: 0.9 }} />
    </Box>
  );
};

/**
 * UploadFileDialog
 *
 * Props:
 *   open             – boolean
 *   onClose          – () => void
 *   onUploadSuccess  – (fileObject) => void   ← receives the full file object:
 *                                               { file_url, url, filename, ... }
 *                                               Extract .file_url or .url for the URL string.
 *   zIndex           – optional number (default: theme.zIndex.modal + 2 = 1302)
 *                      Pass a higher value when used inside a full-screen overlay.
 */
const UploadFileDialog = ({ open, onClose, onUploadSuccess, zIndex: zIndexProp }) => {
  const theme = useTheme();

  // ── State ──────────────────────────────────────────────────────────
  const [uploadedFile,         setUploadedFile]         = useState(null);
  const [selectedExistingFile, setSelectedExistingFile] = useState(null);
  const [error,                setError]                = useState(null);
  const [documentType,         setDocumentType]         = useState('');
  const [uploadProgress,       setUploadProgress]       = useState(0);
  const [isProcessing,         setIsProcessing]         = useState(false);
  const [mode,                 setMode]                 = useState('upload');
  const [activeTab,            setActiveTab]            = useState('all');
  const [searchQuery,          setSearchQuery]          = useState('');

  // ── Effective z-index ─────────────────────────────────────────────
  // Default to modal + 2 (1302) so this always renders above:
  //   - standard MUI Dialogs      (1300)
  //   - our full-screen overlays  (1301)
  const effectiveZIndex = zIndexProp ?? (theme.zIndex.modal + 2);

  // ── File library ──────────────────────────────────────────────────
  const { data: filesResponse } = useQuery(
    'files',
    () => API.files.fetch(''),
    {
      enabled: open && mode === 'select',
      refetchOnWindowFocus: false,
      initialData: []
    }
  );

  const files = useMemo(() => {
    if (!Array.isArray(filesResponse)) return [];
    return filesResponse.map((file) => ({ ...file, fileType: determineFileType(file.filename) }));
  }, [filesResponse]);

  const filteredFiles = useMemo(() => {
    const categoryFiltered = filterFilesByCategory(files, activeTab);
    if (!searchQuery) return categoryFiltered;
    const lowerSearch = searchQuery.toLowerCase();
    return categoryFiltered.filter((file) =>
      (file.name || file.filename || '').toLowerCase().includes(lowerSearch)
    );
  }, [files, activeTab, searchQuery]);

  // ── Upload mutation ───────────────────────────────────────────────
  const { mutate: uploadFile, isLoading } = useMutation(
    (fileData) => client.files.post(fileData),
    {
      onSuccess: (data) => {
        setUploadedFile(data);
        setError(null);
        setUploadProgress(100);
      },
      onError: (error) => {
        setError(error.message || 'Failed to upload file');
        setUploadProgress(0);
        setIsProcessing(false);
      }
    }
  );

  // ── Dropzone ──────────────────────────────────────────────────────
  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      setError(null);
      setUploadProgress(0);
      setUploadedFile(null);

      if (file.size > FILE_SIZE_LIMIT) {
        setError('File size exceeds the 4.5 MB limit. Please upload a smaller file.');
        return;
      }

      const ext = file.name.split('.').pop().toLowerCase();
      let detectedType;

      if (['jpeg', 'jpg', 'png'].includes(ext)) {
        detectedType = 'image';
      } else if (ext === 'pdf') {
        detectedType = 'pdf';
      } else if (ext === 'txt') {
        detectedType = 'file';
      } else {
        setError('Unsupported file type. Please upload JPEG, PNG, PDF, or TXT files.');
        return;
      }

      setDocumentType(detectedType);

      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) { clearInterval(progressInterval); return 90; }
          return prev + Math.random() * 20;
        });
      }, 200);

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64File = reader.result.split(',')[1];
        uploadFile({
          file: { name: file.name, content: base64File, type: file.type, file_type: detectedType }
        });
      };
    },
    [uploadFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled: isLoading || isProcessing,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png'],
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt']
    }
  });

  // ── Handlers ──────────────────────────────────────────────────────
  const handleUseFile = async () => {
    const fileToUse = mode === 'upload' ? uploadedFile : selectedExistingFile;
    if (!fileToUse || isProcessing) return;

    const fileUrl = getFileUrl(fileToUse);
    if (!fileUrl) {
      setError('File URL is missing. Please try selecting a different file.');
      return;
    }

    const normalizedFile = { ...fileToUse, url: fileUrl, file_url: fileUrl };
    setIsProcessing(true);
    setError(null);

    try {
      if (onUploadSuccess) {
        await Promise.resolve(onUploadSuccess(normalizedFile));
      }
      await new Promise((resolve) => setTimeout(resolve, 500));
      setIsProcessing(false);
      handleClose();
    } catch (err) {
      console.error('Error in handleUseFile:', err);
      setError(err?.message || 'Failed to save the file. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (isLoading || isProcessing) return;
    setUploadedFile(null);
    setSelectedExistingFile(null);
    setError(null);
    setUploadProgress(0);
    setDocumentType('');
    setIsProcessing(false);
    setMode('upload');
    setSearchQuery('');
    setActiveTab('all');
    if (onClose) onClose();
  };

  const handleCancel = () => { if (!isLoading) handleClose(); };

  const handleReset = () => {
    setUploadedFile(null);
    setSelectedExistingFile(null);
    setError(null);
    setUploadProgress(0);
    setIsProcessing(false);
  };

  const handleModeChange = (event, newMode) => {
    if (newMode && !isLoading && !isProcessing) {
      setMode(newMode);
      setUploadedFile(null);
      setSelectedExistingFile(null);
      setError(null);
    }
  };

  const handleFileSelect = (file) => {
    const fileUrl = getFileUrl(file);
    if (!fileUrl) {
      setError('This file is missing a URL and cannot be selected.');
      return;
    }
    setSelectedExistingFile(file);
    setError(null);
  };

  const hasSelectedFile = mode === 'upload' ? uploadedFile : selectedExistingFile;
  const isSelectMode = mode === 'select' && !selectedExistingFile;

  // ── Render ────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth={isSelectMode ? 'md' : 'sm'}
      fullWidth
      disableEscapeKeyDown={isLoading}
      // KEY FIX: always render above our full-screen overlays (zIndex.modal + 1 = 1301)
      sx={{ zIndex: effectiveZIndex }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.2)'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', p: 2.5, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', opacity: 0.5 }}/>
        <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          {mode === 'upload' ? <CloudUpload sx={{ fontSize: 28, mr: 1.5 }} /> : <FolderOpen sx={{ fontSize: 28, mr: 1.5 }} />}
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {mode === 'upload' ? 'Upload File' : 'Select from My Files'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
              {hasSelectedFile ? 'Ready to use' : mode === 'upload' ? 'Drag & drop or browse' : 'Choose from your library'}
            </Typography>
          </Box>
          <IconButton onClick={handleCancel} disabled={isLoading}
            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.05)' } }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Mode tabs */}
      <Box sx={{ px: 3, pt: 2, display: 'flex', justifyContent: 'center' }}>
        <Tabs value={mode} onChange={handleModeChange}
          sx={{ minHeight: 42, '& .MuiTabs-indicator': { height: 3, borderRadius: 2 }, '& .MuiTab-root': { minHeight: 42, py: 1 } }}>
          <Tab label="Upload New"       value="upload" icon={<CloudUpload sx={{ fontSize: 18 }} />} iconPosition="start" disabled={isLoading || isProcessing}/>
          <Tab label="Select Existing"  value="select" icon={<FolderOpen  sx={{ fontSize: 18 }} />} iconPosition="start" disabled={isLoading || isProcessing}/>
        </Tabs>
      </Box>
      <Divider sx={{ mt: 1.5 }} />

      <DialogContent sx={{ p: isSelectMode ? 2.5 : 4 }}>
        {mode === 'upload' ? (
          !uploadedFile ? (
            <Box>
              <Box
                {...getRootProps()}
                sx={{
                  border: `3px dashed ${isDragActive ? theme.palette.primary.main : documentType ? (FILE_TYPE_CONFIG[documentType]?.color || theme.palette.divider) : theme.palette.divider}`,
                  borderRadius: 4, p: 6, textAlign: 'center',
                  cursor: isLoading || isProcessing ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: isDragActive
                    ? `linear-gradient(135deg, ${theme.palette.primary.main}08, ${theme.palette.primary.main}04)`
                    : documentType
                    ? `linear-gradient(135deg, ${FILE_TYPE_CONFIG[documentType]?.color || '#667eea'}08, ${FILE_TYPE_CONFIG[documentType]?.color || '#764ba2'}04)`
                    : 'rgba(0,0,0,0.02)',
                  opacity: isLoading || isProcessing ? 0.6 : 1,
                  '&:hover': {
                    borderColor: documentType ? (FILE_TYPE_CONFIG[documentType]?.color || theme.palette.primary.main) : theme.palette.primary.main,
                    transform: (isLoading || isProcessing) ? 'none' : 'translateY(-2px)',
                    boxShadow: (isLoading || isProcessing) ? 'none' : '0 8px 32px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <input {...getInputProps()} />
                <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: `linear-gradient(135deg, ${FILE_TYPE_CONFIG[documentType]?.color || '#667eea'}22, ${FILE_TYPE_CONFIG[documentType]?.color || '#764ba2'}11)`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                  <CloudUpload sx={{ fontSize: 40, color: FILE_TYPE_CONFIG[documentType]?.color || theme.palette.primary.main, opacity: 0.8 }}/>
                </Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  {isDragActive ? 'Drop your file here' : 'Drag & drop or click to browse'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supported: JPEG, PNG, PDF, TXT · Max size: 4.5 MB
                </Typography>
              </Box>

              {isLoading && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>Uploading… {Math.round(uploadProgress)}%</Typography>
                    <CircularProgress size={20}/>
                  </Box>
                  <LinearProgress variant="determinate" value={uploadProgress}
                    sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.1)', '& .MuiLinearProgress-bar': { borderRadius: 3, background: `linear-gradient(90deg, ${FILE_TYPE_CONFIG[documentType]?.color || theme.palette.primary.main}, ${FILE_TYPE_CONFIG[documentType]?.color || theme.palette.primary.main}88)` } }}/>
                </Box>
              )}
              {error && <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>{error}</Alert>}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 100, height: 100, borderRadius: '50%', background: 'linear-gradient(135deg, #4CAF50, #8BC34A)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%': { boxShadow: '0 0 0 0 rgba(76,175,80,0.4)' }, '70%': { boxShadow: '0 0 0 20px rgba(76,175,80,0)' }, '100%': { boxShadow: '0 0 0 0 rgba(76,175,80,0)' } } }}>
                <CheckCircle sx={{ fontSize: 48, color: 'white' }}/>
              </Box>
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, color: '#4CAF50' }}>Upload Successful!</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {isProcessing ? 'Saving your file…' : 'Click "Use This File" to confirm'}
              </Typography>
              <FilePreview url={getFileUrl(uploadedFile)} fileName={uploadedFile.name}/>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-all', color: theme.palette.primary.main, fontWeight: 500 }}>
                  {(getFileUrl(uploadedFile) || '').split('/').pop()}
                </Typography>
              </Box>
              {isProcessing && <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}><CircularProgress size={18}/><Typography variant="body2" color="text.secondary">Saving…</Typography></Box>}
              {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </Box>
          )
        ) : (
          !selectedExistingFile ? (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
                  <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto"
                    sx={{ flexGrow: 1, minHeight: 40, '& .MuiTabs-indicator': { height: 3, borderRadius: 2 }, '& .MuiTab-root': { minHeight: 40, py: 0.5, minWidth: 60 } }}>
                    <Tab label="All"    value="all"  />
                    <Tab label="PDF"    value="pdf"  />
                    <Tab label="Images" value="image"/>
                    <Tab label="Misc"   value="misc" />
                  </Tabs>
                  <TextField size="small" variant="outlined" placeholder="Search…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ minWidth: 160 }}
                    InputProps={{ endAdornment: <Search sx={{ color: 'text.secondary', fontSize: 20 }}/> }}/>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{activeTab === 'all' ? 'All Files' : activeTab.toUpperCase()}</Typography>
                  <Chip label={`${filteredFiles.length}`} size="small" sx={{ height: 20, fontSize: '0.75rem' }}/>
                </Box>
              </Box>

              <Box sx={{ maxHeight: '400px', overflowY: 'auto', pr: 0.5 }}>
                {filteredFiles.length > 0 ? (
                  <Grid container spacing={1.5}>
                    {filteredFiles.map((file) => (
                      <Grid item xs={6} sm={4} key={file.id}>
                        <Card onClick={() => handleFileSelect(file)}
                          sx={{ cursor: 'pointer', transition: 'all 0.2s', height: '100%', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3, bgcolor: 'action.hover' } }}>
                          <CardContent sx={{ p: 1.5, textAlign: 'center', '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'center' }}><FileLibraryIcon file={file}/></Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.2, minHeight: '2.4em' }}>
                              {file.filename}
                            </Typography>
                            <Chip label={(file.fileType || 'FILE').toUpperCase()} size="small" sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }}/>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <FolderOpen sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }}/>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 0.5 }}>No files found</Typography>
                    <Typography variant="body2" color="text.disabled">{searchQuery ? 'Try a different search' : 'Upload files to get started'}</Typography>
                  </Box>
                )}
              </Box>
              {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ width: 100, height: 100, borderRadius: '50%', background: 'linear-gradient(135deg, #4CAF50, #8BC34A)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%': { boxShadow: '0 0 0 0 rgba(76,175,80,0.4)' }, '70%': { boxShadow: '0 0 0 20px rgba(76,175,80,0)' }, '100%': { boxShadow: '0 0 0 0 rgba(76,175,80,0)' } } }}>
                <CheckCircle sx={{ fontSize: 48, color: 'white' }}/>
              </Box>
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, color: '#4CAF50' }}>File Selected!</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {isProcessing ? 'Saving your file…' : 'Click "Use This File" to confirm'}
              </Typography>
              <FilePreview url={getFileUrl(selectedExistingFile)} fileName={selectedExistingFile.filename}/>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-all', color: theme.palette.primary.main, fontWeight: 500 }}>
                  {selectedExistingFile.filename}
                </Typography>
              </Box>
              {isProcessing && <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}><CircularProgress size={18}/><Typography variant="body2" color="text.secondary">Saving…</Typography></Box>}
              {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </Box>
          )
        )}
      </DialogContent>

      {/* Actions */}
      <Box sx={{ p: 2.5, pt: 0, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <Button onClick={handleCancel} variant="outlined" disabled={isLoading} sx={{ borderRadius: 2, minWidth: 90 }}>Cancel</Button>
        {hasSelectedFile && (
          <>
            <Button onClick={handleReset} variant="outlined" disabled={isProcessing} sx={{ borderRadius: 2, minWidth: 110 }}>
              {mode === 'upload' ? 'Upload Different' : 'Select Different'}
            </Button>
            <Button onClick={handleUseFile} variant="contained" disabled={isProcessing}
              startIcon={isProcessing ? <CircularProgress size={16} color="inherit"/> : null}
              sx={{ borderRadius: 2, minWidth: 130, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', '&:hover': { background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)' }, '&.Mui-disabled': { background: 'linear-gradient(135deg, #a0a0a0 0%, #808080 100%)', color: 'rgba(255,255,255,0.5)' } }}>
              {isProcessing ? 'Saving…' : 'Use This File'}
            </Button>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default UploadFileDialog;